import { Injectable, inject } from '@angular/core';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/field.service';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { ElectronicFieldPolygonService } from './electronic-field-polygon.service';
import { MapClickField, RequestGeoJsonGeometry } from '../utils/map-click-context.models';
import { isValidMapCoordinate } from '../utils/processing-request-params.util';

/** Longest wait for the field lookup before the form opens with coordinates only. */
export const FIELD_LOOKUP_TIMEOUT_MS = 1500;

const WGS84_SRID = 4326;

/**
 * Finds the field under a clicked map point through the existing
 * `GET /cropMonitoring/Field/GetByPoints` (generated client). Never errors: a missing field (404),
 * any failure and a timeout all yield null, so the form opens with coordinates only.
 * The request is a GET: unsubscribing from the returned observable cancels it.
 */
@Injectable({ providedIn: 'root' })
export class MapClickFieldLookupService {
  private readonly fields = inject(FieldService);
  private readonly polygons = inject(ElectronicFieldPolygonService);

  findFieldAtPoint(lat: number, lng: number): Observable<MapClickField | null> {
    return this.fields.cropMonitoringFieldGetByPointsGet(lng, lat, WGS84_SRID).pipe(
      timeout(FIELD_LOOKUP_TIMEOUT_MS),
      map((field) => this.toMapClickField(field)),
      catchError(() => of(null)),
    );
  }

  private toMapClickField(field: { id?: string; name?: string | null; polygonJson?: string | null } | null): MapClickField | null {
    if (!field?.id) {
      return null;
    }
    const boundary = this.toGeometry(field.polygonJson);
    return { id: field.id, name: field.name?.trim() || field.id, ...(boundary ? { boundary } : {}) };
  }

  /** Reads GeoJSON (geometry or feature) or WKT; anything else means "no boundary". */
  private toGeometry(raw: string | null | undefined): RequestGeoJsonGeometry | undefined {
    if (!raw?.trim()) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(raw);
      const geometry = parsed?.type === 'Feature' ? parsed.geometry : parsed;
      if (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon') {
        // A malformed GeoJSON boundary must not reach the server (400): fall back to the point square.
        return isValidGeometry(geometry) ? { type: geometry.type, coordinates: geometry.coordinates } : undefined;
      }
      return undefined;
    } catch {
      // Not JSON: try WKT below.
    }
    // WKT: only a single POLYGON is understood; MULTIPOLYGON would be flattened into a bogus ring.
    if (!/^\s*POLYGON\b/i.test(raw)) {
      return undefined;
    }
    const points = this.polygons.parsePolygonData(raw);
    if (!points || points.length < 3) {
      return undefined;
    }
    const ring = points.map(([lng, lat]) => [lng, lat]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
    return isValidRing(ring) ? { type: 'Polygon', coordinates: [ring] } : undefined;
  }
}

const MIN_RING_POSITIONS = 4;

function isValidPosition(position: unknown): boolean {
  return Array.isArray(position) && position.length >= 2 && isValidMapCoordinate(position[1], position[0]);
}

function isValidRing(ring: unknown): boolean {
  return Array.isArray(ring) && ring.length >= MIN_RING_POSITIONS && ring.every(isValidPosition);
}

function isValidPolygon(rings: unknown): boolean {
  return Array.isArray(rings) && rings.length > 0 && rings.every(isValidRing);
}

function isValidGeometry(geometry: { type: string; coordinates: unknown }): boolean {
  if (geometry.type === 'Polygon') {
    return isValidPolygon(geometry.coordinates);
  }
  return Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates.every(isValidPolygon);
}
